/* Floating sticky search — desktop + mobile
   - Starts closed on page load (only opens via floating button)
   - Predictive suggestions (Shopify /search/suggest.json)
   - Thumbnails robustly resolved from multiple fields
   - Enter/submit: filter grid if present; otherwise go to /search
*/

(() => {
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const money = (cents, cc) => {
    if (typeof cents !== 'number') return '';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cc || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(cents / 100); }
    catch { return (cents/100).toFixed(2); }
  };

  const root       = $('#osu-fsearch');
  const overlay    = $('#osu-fsearch-overlay');
  const panel      = $('#osu-fsearch-panel');
  const form       = $('#osu-fsearch-form');
  const input      = $('#osu-fsearch-input');
  const resultsBox = $('#osu-fsearch-results');

  if (!root || !panel || !input) return;

  // ---------- Closed initial state ----------
  const ensureClosed = () => {
    root.classList.remove('open', 'has-results');
    root.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.style.display = 'none';
    panel.style.display = 'none';
    resultsBox.innerHTML = '';
    input.value = '';
    document.documentElement.style.overflow = '';
  };
  ensureClosed();

  // ---------- Open / Close ----------
  const openSearch = () => {
    root.classList.add('open');
    root.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.style.display = 'block';
    panel.style.display = 'block';
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => input.focus(), 30);
  };
  const closeSearch = () => ensureClosed();

  // Floating button + any other open triggers
  const launchers = ['[data-fsearch-open]', '#osu-fsearch-fab', '.header__icon--search', 'button[aria-label="Search"]'];
  $$(launchers.join(',')).forEach(btn => on(btn, 'click', (e) => { e.preventDefault(); openSearch(); }));

  // Close logic (overlay tap or Esc)
  on(overlay, 'mousedown', (e) => { if (e.target === overlay) closeSearch(); });
  on(document, 'keydown', (e) => { if (e.key === 'Escape') closeSearch(); });
  on(panel, 'mousedown', (e) => e.stopPropagation());
  on(panel, 'click', (e) => e.stopPropagation());

  // ---------- Predictive suggestions ----------
  // Ask for product resources; try to be generous with options so images are present.
// Minimal /search/suggest.json URL that works across stores (avoid 422)
const suggestUrl = (q) =>
  `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`;

  const fetchSuggestions = async (q) => {
    const res = await fetch(suggestUrl(q), { headers: { 'Accept': 'application/json' }});
    if (!res.ok) throw new Error('suggest fetch failed');
    return res.json();
  };

  // Safely resolve a thumbnail URL from several possible fields
  const getThumb = (p) => {
    const url =
      p?.image?.url || p?.image?.src ||
      p?.featured_image?.url || p?.featured_image ||
      (Array.isArray(p?.images) && (p.images[0]?.url || p.images[0]?.src)) ||
      (p?.variants && p.variants[0]?.image && (p.variants[0].image.url || p.variants[0].image.src)) ||
      '';
    if (!url) return '';
    // Add a small size if it's a Shopify CDN image
    try {
      const u = new URL(url, location.origin);
      if (/cdn\.shopify\.com/.test(u.hostname)) return url; // already good
      return url;
    } catch {
      // might be protocol-less or path-only
      return url.startsWith('//') ? url : ('//'+url.replace(/^https?:\/\//,''));
    }
  };

  let t;
  const renderSuggestions = (json) => {
    const prods = json?.resources?.results?.products || [];
    if (!prods.length) {
      root.classList.remove('has-results');
      resultsBox.innerHTML = '';
      return;
    }
    root.classList.add('has-results');
    resultsBox.innerHTML = `
      <ul class="osu-fs__list">
        ${prods.map(p => {
          const img = getThumb(p);
          const price = (p.price_min ?? p.price ?? 0);
          return `
            <li class="osu-fs__item" role="option">
              <a class="osu-fs__link" href="${p.url}">
                <img class="osu-fs__thumb" ${img ? `src="${img}"` : 'hidden'} alt="">
                <div class="osu-fs__meta">
                  <div class="osu-fs__title">${p.title}</div>
                </div>
                <div class="osu-fs__price">${money(price, p.currency)}</div>
              </a>
            </li>`;
        }).join('')}
      </ul>
    `;
  };

  on(input, 'input', () => {
    const q = input.value.trim();
    clearTimeout(t);
    if (!q) { root.classList.remove('has-results'); resultsBox.innerHTML = ''; return; }
    t = setTimeout(async () => {
      try { const json = await fetchSuggestions(q); renderSuggestions(json); }
      catch { /* silent */ }
    }, 160);
  });

  // Suggestion click navigates immediately
  on(resultsBox, 'pointerdown', (e) => {
    const a = e.target.closest('a[href]');
    if (!a) return;
    e.preventDefault();
    e.stopPropagation();
    location.assign(a.getAttribute('href'));
  }, true);

  // ---------- Grid filter (fallback to search page) ----------
  const getGrid = () => {
    const candidates = [
      '.shopify-section .product-grid',
      '.shopify-section .grid--view-items',
      '.shopify-section .collection-products',
      '.shopify-section [role="list"].grid',
      '.featured-collection [role="list"]',
      '.featured-collection .grid',
    ];
    for (const sel of candidates) {
      const g = $(sel);
      if (g && g.children.length) return g;
    }
    return null;
  };

  const titleFromCard = (card) => {
    const t = $('.card__heading a, .card__heading, .product-card h3 a, .product-card h3, .break-words a, .break-words', card);
    return (t?.textContent || '').trim().toLowerCase();
  };

  const filterGrid = (q) => {
    const grid = getGrid();
    if (!grid) return false;
    const needle = q.trim().toLowerCase();
    if (!needle) return false;

    let shown = 0;
    Array.from(grid.children).forEach(li => {
      const match = titleFromCard(li).includes(needle);
      li.style.display = match ? '' : 'none';
      if (match) shown++;
    });

    // Tiny counter
    let badge = $('#osu-fsearch-count');
    if (!badge) {
      badge = document.createElement('div');
      badge.id = 'osu-fsearch-count';
      badge.style.cssText = 'position:fixed;left:16px;bottom:16px;z-index:2147483647;background:#fff;border:1px solid #ddd;border-radius:10px;padding:8px 10px;font-weight:600';
      document.body.appendChild(badge);
    }
    badge.textContent = `Showing ${shown} result${shown === 1 ? '' : 's'}`;
    return true;
  };

  const gotoSearchPage = (q) => {
    const url = `/search?q=${encodeURIComponent(q)}&type=product&options[prefix]=last`;
    location.assign(url);
  };

  const handleSearch = (q) => {
    if (!q || !q.trim()) return;
    const didFilter = filterGrid(q);
    if (!didFilter) gotoSearchPage(q);
    closeSearch();
  };

  // Submit / Enter
  on(form, 'submit', (e) => { e.preventDefault(); e.stopPropagation(); handleSearch(input.value); });
  on(input, 'keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleSearch(input.value); } });

  // Expose for quick testing
  window.__osuSearch = { open: openSearch, close: closeSearch };
})();
